import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import DynamicTable from "../../components/DynamicTable";
import NoteModal from "../../components/NoteModal";
import { useTenant } from "../../context/TenantContext";
import {
  fetchGeneratedNoteDocument,
  getNotesAddedYesNo,
  notesAddedDisplay,
} from "../../services/generatedNotePresence";
import {
  formatEvaluationFormTypeDisplay,
  inferEvaluationFormCompletedBy,
} from "./evaluationFormRole";
import { listDateFilterPayload } from "./listDateFilters";
import {
  MINDTALK_LIST_STATE_FILTER_OPTIONS,
  mindtalkConsultationStateColumnLabel,
  mindtalkStateFilterValueForApi,
} from "./mindtalkConsultationState";
import styles from "./styles.module.css";

/** Mindtalk-only allied Odoo session lists (same models as CDC doctor app). */
export const ALLIED_SESSION_KINDS = [
  "special_educator",
  "occupational_therapist",
  "speech_therapist",
  "rehabilitation_psychologist",
  "pediatric_physiotherapist",
];

const ALLIED_SESSION_KIND_SET = new Set(ALLIED_SESSION_KINDS);

const ALLIED_ODOO_OBJECT = {
  special_educator: "special.educator",
  occupational_therapist: "occupational.therapist",
  speech_therapist: "speech.therapist",
  rehabilitation_psychologist: "rehabilitation.psychologist",
  pediatric_physiotherapist: "pediatric.physiotherapist",
};

const CONSULTATION_LIST_FETCHERS = {
  psychiatrist: (ps) => ps.fetchPsychiatristConsultationsList,
  psychologist: (ps) => ps.fetchPsychologistConsultationsList,
  special_educator: (ps) => ps.fetchSpecialEducatorSessionsList,
  occupational_therapist: (ps) => ps.fetchOccupationalTherapistSessionsList,
  speech_therapist: (ps) => ps.fetchSpeechTherapistSessionsList,
  rehabilitation_psychologist: (ps) =>
    ps.fetchRehabilitationPsychologistSessionsList,
  pediatric_physiotherapist: (ps) => ps.fetchPediatricPhysiotherapistSessionsList,
};

/** Exact-count counterpart of CONSULTATION_LIST_FETCHERS. See patientService.countList(). */
const CONSULTATION_COUNT_FETCHERS = {
  psychiatrist: (ps) => ps.countPsychiatristConsultations,
  psychologist: (ps) => ps.countPsychologistConsultations,
  special_educator: (ps) => ps.countSpecialEducatorSessions,
  occupational_therapist: (ps) => ps.countOccupationalTherapistSessions,
  speech_therapist: (ps) => ps.countSpeechTherapistSessions,
  rehabilitation_psychologist: (ps) => ps.countRehabilitationPsychologistSessions,
  pediatric_physiotherapist: (ps) => ps.countPediatricPhysiotherapistSessions,
};

function isAlliedSessionKind(k) {
  return ALLIED_SESSION_KIND_SET.has(k);
}

function clinicianFilterLabel(kind) {
  if (kind === "psychiatrist") return "Psychiatrist";
  if (kind === "psychologist") return "Psychologist";
  const labels = {
    special_educator: "Special educator",
    occupational_therapist: "Occupational therapist",
    speech_therapist: "Speech therapist",
    rehabilitation_psychologist: "Rehabilitation psychologist",
    pediatric_physiotherapist: "Pediatric physiotherapist",
  };
  return labels[kind] || "Clinician";
}

function noteKindForGeneratedNotes(kind) {
  if (kind === "psychiatrist") return "psychiatrist";
  if (kind === "psychologist") return "psychologist";
  return kind;
}

const PER_PAGE = 10;
/**
 * When Group By is on we want a larger window so groups are meaningful — but Odoo's REST
 * endpoint silently caps `per_page` around ~50 on the Cadabams ERP. Asking for 200 makes
 * the totalPages math lie ("1 of 1") while the backend hands back ~50 records, and the
 * Next button (when reachable) skips past the result set causing a 500. 50 is below the
 * observed cap and lets standard offset-based pagination work cleanly across grouped pages.
 */
const GROUP_BY_PAGE_SIZE = 50;

const GROUP_BY_FIELD_ORDER = [
  "subcampus",
  "doctor",
  "patient",
  "date",
  "state",
  "type",
  "notes_generated",
];

function sortGroupByFields(selected) {
  const set = new Set(selected);
  return GROUP_BY_FIELD_ORDER.filter((f) => set.has(f));
}

function monthKeyFromDateRaw(raw) {
  if (raw == null || raw === "") return "m:__none__";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return `m:raw:${String(raw)}`;
  const y = d.getFullYear();
  const mo = d.getMonth() + 1;
  return `m:${y}-${String(mo).padStart(2, "0")}`;
}

function monthLabelFromDateRaw(raw) {
  if (raw == null || raw === "") return "(No date)";
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return String(raw);
  return d.toLocaleString(undefined, { month: "long", year: "numeric" });
}

function odooRelationLabel(rel) {
  if (rel == null || rel === false) return "";
  if (Array.isArray(rel) && rel[1] != null) {
    const s = String(rel[1]);
    return s.split("[")[0]?.trim() || s;
  }
  return "";
}

function consultationGroupByConfig(kind, field) {
  if (!field) return null;
  if (field === "subcampus") {
    // Mindtalk consultations carry `campus_id` (subcampus); other tenants fall back to
    // `company_id`. Rows missing both bucket into "(No campus)".
    return {
      groupId: (r) => {
        const rel = r.campus_id || r.company_id;
        if (Array.isArray(rel) && rel[0] != null) return `c:${rel[0]}`;
        return "c:none";
      },
      headerLabel: (r) => {
        const rel = r.campus_id || r.company_id;
        if (Array.isArray(rel) && rel[1] != null) {
          const s = String(rel[1]);
          return s.split("[")[0]?.trim() || s;
        }
        return "(No campus)";
      },
    };
  }
  if (field === "patient") {
    return {
      groupId: (r) =>
        r.patient_id?.[0] != null
          ? `p:${r.patient_id[0]}`
          : `pname:${(r.patient || "").trim() || "__none__"}`,
      headerLabel: (r) => (r.patient || "").trim() || "(No patient)",
    };
  }
  if (field === "doctor") {
    return {
      groupId: (r) => {
        const rel =
          kind === "psychiatrist" ? r.psychiatrist_id : r.clinical_psychologist_id;
        if (Array.isArray(rel) && rel[0] != null) return `d:${rel[0]}`;
        return "d:none";
      },
      headerLabel: (r) => (r.doctor_name || "").trim() || "(No clinician)",
    };
  }
  if (field === "state") {
    return {
      groupId: (r) => `s:${String(r.state ?? "")}`,
      headerLabel: (r) => String(r.state ?? "—"),
    };
  }
  if (field === "type") {
    return {
      groupId: (r) => `t:${String(r.consultation_type ?? "").trim() || "__"}`,
      headerLabel: (r) =>
        String(r.consultation_type ?? "").trim() || "(No type)",
    };
  }
  if (field === "date") {
    return {
      groupId: (r) => monthKeyFromDateRaw(r.dateRaw),
      headerLabel: (r) => monthLabelFromDateRaw(r.dateRaw),
    };
  }
  if (field === "notes_generated") {
    return {
      groupId: (r) => (r.notesAdded === "Yes" ? "ng:yes" : "ng:no"),
      headerLabel: (r) => (r.notesAdded === "Yes" ? "Yes" : "No"),
      groupSortOrder: (r) => (r.notesAdded === "Yes" ? 0 : 1),
    };
  }
  return null;
}

const emptyFilters = () => ({
  date: "all",
  selectedDate: null,
  /** Used when `date === "custom_range"` (YYYY-MM-DD inclusive bounds). */
  dateFrom: null,
  dateTo: null,
  state: "all",
  doctorId: null,
  consultationChannel: "all",
  campusId: "",
  patientId: null,
});

function stateChipColor(label) {
  const s = String(label || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "draft") return "default";
  if (s === "check in" || s === "check_in") return "info";
  if (s === "check out" || s === "ended") return "warning";
  return "default";
}

function StateChip({ label }) {
  if (!label) return <span style={{ color: "#9ca3af" }}>—</span>;
  return <Chip size="small" label={label} color={stateChipColor(label)} variant="filled" />;
}

function NotesChip({ value }) {
  if (value === "Yes")
    return <Chip size="small" label="Yes" color="success" sx={{ minWidth: 48 }} />;
  return (
    <Chip
      size="small"
      label="No"
      variant="outlined"
      sx={{ minWidth: 48, color: "text.secondary" }}
    />
  );
}

/**
 * Pull the best display datetime from an Odoo row. The `date` column on
 * Mindtalk's `consultation.consultation` and session models is often a
 * date-only value (or midnight-UTC), so rendering it as a datetime would
 * always read "5:30 AM IST". Prefer the explicit datetime fields when present.
 */
function pickConsultationDatetime(row) {
  const candidates = [
    row.consultation_date,
    row.appointment_date,
    row.scheduled_date,
    row.start_date,
    row.start_datetime,
    row.from_datetime,
    row.session_date,
    row.create_date,
    row.date,
  ];
  for (const v of candidates) {
    if (v != null && v !== false && String(v).trim() !== "") return v;
  }
  return null;
}

function formatDisplayDate(raw, tz) {
  if (raw == null || raw === "") return "";
  const s = String(raw);
  // Date-only "YYYY-MM-DD" — render without a misleading time.
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d).toLocaleDateString(undefined, {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  }
  // Odoo returns naive "YYYY-MM-DD HH:mm:ss" datetimes that are actually UTC.
  // Convert by appending the Z so the parser treats them as UTC, then format in tz.
  const utcish = /^\d{4}-\d{2}-\d{2}[ T]\d{2}:\d{2}:\d{2}$/.test(s)
    ? s.replace(" ", "T") + "Z"
    : s;
  const d = new Date(utcish);
  if (Number.isNaN(d.getTime())) return s;
  const opts = tz ? { timeZone: tz } : {};
  return d.toLocaleString(undefined, opts);
}

function formatConsultationRow(row, kind, tz) {
  const patient =
    row.patient_id?.[1]?.split("[")[0]?.trim() || row.patient || "";
  const idDisplay = row.name != null ? String(row.name) : String(row.id ?? "");
  const dateRaw = pickConsultationDatetime(row);
  const dateStr = formatDisplayDate(dateRaw, tz);
  const backendState = row.state ?? "";
  const doctorName =
    kind === "psychiatrist"
      ? odooRelationLabel(row.psychiatrist_id)
      : odooRelationLabel(row.clinical_psychologist_id);
  return {
    ...row,
    name: idDisplay,
    patient,
    doctor_name: doctorName,
    dateRaw: dateRaw ?? null,
    date: dateStr,
    consultation_type: row.consultation_type ?? "",
    state: mindtalkConsultationStateColumnLabel(kind, backendState),
  };
}

export default function ConsultationListSection({
  kind,
  patientService,
  doctorOptions,
  onSelectConsultation,
  psychiatrists = [],
  psychologists = [],
}) {
  const { db, tenant } = useTenant();
  const useSubcampusFilterLabels =
    tenant?.features?.consultationFilterSubcampusLabels === true;
  const subcampusSelectLabel = useSubcampusFilterLabels ? "Subcampus" : "Campus";
  const allSubcampusesLabel = useSubcampusFilterLabels
    ? "All subcampuses"
    : "All campuses";

  const [rows, setRows] = useState([]);
  const [campusOptions, setCampusOptions] = useState(() => [
    { value: "", label: "All campuses" },
  ]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(emptyFilters);
  const [patientSelectOptions, setPatientSelectOptions] = useState([]);
  const prevKindRef = useRef(null);

  const [viewNoteModalOpen, setViewNoteModalOpen] = useState(false);
  const [viewNotePayload, setViewNotePayload] = useState(null);
  const [groupByFields, setGroupByFields] = useState([]);

  const useMindtalkFullDoctorUi =
    tenant?.features?.hrEmployeeDoctorLists === "all_roles";
  const displayTimeZone = tenant?.features?.displayTimeZone;

  const formatTimestamp = useCallback(
    (timestamp) => {
      if (timestamp == null) return "N/A";
      if (typeof timestamp === "string") {
        const d = new Date(timestamp);
        if (Number.isNaN(d.getTime())) return timestamp;
        const opts = displayTimeZone ? { timeZone: displayTimeZone } : {};
        return d.toLocaleString(undefined, opts);
      }
      if (timestamp instanceof Date) {
        const opts = displayTimeZone ? { timeZone: displayTimeZone } : {};
        return timestamp.toLocaleString(undefined, opts);
      }
      return "N/A";
    },
    [displayTimeZone],
  );

  const formatType = useCallback(
    (type) => {
      if (!type) return "N/A";
      if (!useMindtalkFullDoctorUi) {
        const typeMap = {
          psychiatric: "Psychiatric",
          psychologist: "Psychologist",
          evaluationform: "Evaluation Form",
        };
        return typeMap[type] || type;
      }
      const typeMap = {
        psychiatric: "Psychiatric",
        psychologist: "Psychologist",
        special_educator: "Special Educator",
        occupational_therapist: "Occupational Therapist",
        speech_therapist: "Speech Therapist",
        rehabilitation_psychologist: "Rehabilitation Psychologist",
        pediatric_physiotherapist: "Pediatric Physiotherapist",
        evaluationform: "Evaluation Form",
      };
      return typeMap[type] || type;
    },
    [useMindtalkFullDoctorUi],
  );

  const buildNoteForModal = useCallback(
    (raw, consultationId) => {
      const rawType = raw.type;
      const typeDisplay =
        rawType === "evaluationform"
          ? formatEvaluationFormTypeDisplay(raw, psychiatrists, psychologists)
          : formatType(rawType);
      const evaluationFormCompletedBy =
        rawType === "evaluationform"
          ? inferEvaluationFormCompletedBy(raw, psychiatrists, psychologists)
          : undefined;
      const idDisplay =
        raw.id != null && raw.id !== ""
          ? String(raw.id)
          : String(consultationId ?? "");
      const body =
        raw.note ??
        raw.generated_note ??
        raw.content ??
        (typeof raw.text === "string" ? raw.text : "");
      return {
        ...raw,
        typeRaw: rawType,
        type: typeDisplay,
        evaluationFormCompletedBy,
        id: idDisplay,
        created: formatTimestamp(raw.created),
        updated: formatTimestamp(raw.updated),
        note:
          typeof body === "string"
            ? body
            : body != null
              ? String(body)
              : "",
      };
    },
    [formatTimestamp, formatType, psychiatrists, psychologists],
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await patientService.fetchCampusList();
        const fallbackPrefix = useSubcampusFilterLabels ? "Subcampus" : "Campus";
        const opts = [
          { value: "", label: allSubcampusesLabel },
          ...(list || []).map((c) => ({
            value: String(c.id),
            label:
              c.name != null && String(c.name).trim() !== ""
                ? String(c.name)
                : `${fallbackPrefix} #${c.id}`,
          })),
        ];
        if (!cancelled) setCampusOptions(opts);
      } catch (e) {
        console.warn("[ConsultationListSection] campus list", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientService, tenant?.id, useSubcampusFilterLabels, allSubcampusesLabel]);

  useEffect(() => {
    setCampusOptions((prev) => {
      if (!prev?.length) return [{ value: "", label: allSubcampusesLabel }];
      const [, ...rest] = prev;
      return [{ value: "", label: allSubcampusesLabel }, ...rest];
    });
  }, [allSubcampusesLabel]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const patientData = await patientService.getPatients();
        const rows = (Array.isArray(patientData) ? patientData : []).map((p) => {
          if (Array.isArray(p)) return { id: p[0], name: p[1] };
          if (p && typeof p === "object") return { id: p.id, name: p.name };
          return { id: undefined, name: undefined };
        });
        const opts = rows
          .filter((row) => row.id != null && row.id !== "")
          .map((row) => {
            const rawId = row.id;
            const idNum =
              typeof rawId === "number"
                ? rawId
                : /^\d+$/.test(String(rawId).trim())
                  ? parseInt(String(rawId).trim(), 10)
                  : rawId;
            const label =
              row.name != null && String(row.name).trim() !== ""
                ? String(row.name).trim()
                : `Patient #${rawId}`;
            return { value: idNum, label };
          });
        if (!cancelled) setPatientSelectOptions(opts);
      } catch (e) {
        console.warn("[ConsultationListSection] patient list", e);
        if (!cancelled) setPatientSelectOptions([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [patientService, tenant?.id]);

  useEffect(() => {
    if (prevKindRef.current !== null && prevKindRef.current !== kind) {
      setFilters((prev) => ({ ...prev, state: "all" }));
      setCurrentPage(1);
    }
    prevKindRef.current = kind;
  }, [kind]);

  const listFilters = useMemo(() => {
    const datePart = listDateFilterPayload(filters);
    const stateForApi = mindtalkStateFilterValueForApi(kind, filters.state);
    return {
      state: stateForApi,
      ...datePart,
      doctorId:
        filters.doctorId != null && filters.doctorId !== ""
          ? filters.doctorId
          : undefined,
      consultationChannel: filters.consultationChannel,
      campusId: filters.campusId || undefined,
      patientId:
        filters.patientId != null && filters.patientId !== ""
          ? filters.patientId
          : undefined,
    };
  }, [filters, kind]);

  const listFiltersForApi = useMemo(() => {
    if (!isAlliedSessionKind(kind)) return listFilters;
    return {
      state: listFilters.state,
      selectedDate: listFilters.selectedDate,
      dateFrom: listFilters.dateFrom,
      dateTo: listFilters.dateTo,
      doctorId: listFilters.doctorId,
      patientId: listFilters.patientId,
    };
  }, [kind, listFilters]);

  const listPageSize = useMemo(
    () => (groupByFields.length > 0 ? GROUP_BY_PAGE_SIZE : PER_PAGE),
    [groupByFields.length],
  );

  const filtersActive = useMemo(
    () =>
      Boolean(
        (filters.date && filters.date !== "all") ||
          (filters.state && filters.state !== "all") ||
          (filters.consultationChannel && filters.consultationChannel !== "all") ||
          filters.campusId ||
          filters.doctorId ||
          filters.patientId,
      ),
    [filters],
  );

  const groupByLevels = useMemo(() => {
    return sortGroupByFields(groupByFields)
      .map((f) => consultationGroupByConfig(kind, f))
      .filter(Boolean);
  }, [kind, groupByFields]);

  const groupByMenuItems = useMemo(
    () => [
      { value: "subcampus", label: subcampusSelectLabel },
      { value: "doctor", label: clinicianFilterLabel(kind) },
      { value: "patient", label: "Patient" },
      { value: "date", label: "Date (month)" },
      { value: "state", label: "State" },
      { value: "type", label: "Session type" },
      { value: "notes_generated", label: "Notes generated (Yes / No)" },
    ],
    [kind, subcampusSelectLabel],
  );

  const sectionTitle = useMemo(() => {
    if (kind === "psychiatrist") return "Psychiatrist consultations";
    if (kind === "psychologist") return "Psychologist consultations";
    const titles = {
      special_educator: "Special educator sessions",
      occupational_therapist: "Occupational therapist sessions",
      speech_therapist: "Speech therapist sessions",
      rehabilitation_psychologist: "Rehabilitation psychologist sessions",
      pediatric_physiotherapist: "Pediatric physiotherapist sessions",
    };
    return titles[kind] || "Sessions";
  }, [kind]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const listFn = CONSULTATION_LIST_FETCHERS[kind];
      if (typeof listFn !== "function") {
        throw new Error(`Unknown consultation kind: ${kind}`);
      }
      const api = listFn(patientService);
      const countFn = CONSULTATION_COUNT_FETCHERS[kind]?.(patientService);

      // The server's total_records ignores `domain`, so for a filtered view we count the
      // matching ids ourselves. Fire it alongside the page request, not after it.
      const [{ data, total }, exactCount] = await Promise.all([
        api(currentPage, listPageSize, listFiltersForApi),
        filtersActive && typeof countFn === "function"
          ? countFn(listFiltersForApi)
          : Promise.resolve(null),
      ]);
      const sorted = [...(data || [])].sort((a, b) => {
        const da = a.date ? new Date(a.date) : new Date(0);
        const dbb = b.date ? new Date(b.date) : new Date(0);
        return dbb - da;
      });
      const baseRows = sorted.map((row) =>
        formatConsultationRow(row, kind, displayTimeZone),
      );
      const notesFlags = await Promise.all(
        sorted.map((row) =>
          getNotesAddedYesNo(db, row.id, noteKindForGeneratedNotes(kind), row.patient_id?.[0]),
        ),
      );
      setRows(
        baseRows.map((r, i) => {
          const yesNo = notesAddedDisplay(notesFlags[i]);
          return {
            ...r,
            // Keep the raw string for Group By + showIf logic, render as a chip for the cell.
            notesAdded: yesNo,
            notesAddedCell: <NotesChip value={yesNo} />,
            stateCell: <StateChip label={r.state} />,
          };
        }),
      );
      if (filtersActive) {
        // `null` = probe hit its cap; fall back to "at least N" paging.
        setTotalRecords(exactCount);
      } else {
        setTotalRecords(typeof total === "number" ? total : sorted.length);
      }
    } catch (e) {
      console.error("[ConsultationListSection]", e);
      setError(e?.message || "Failed to load consultations");
      setRows([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  }, [
    kind,
    patientService,
    currentPage,
    listPageSize,
    listFiltersForApi,
    filtersActive,
    db,
    displayTimeZone,
  ]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  /**
   * The backend returns the same `total_records` regardless of the `domain` filter, so its
   * page count is only meaningful for an unfiltered list — it reported "1 of 1121" for a
   * single-patient filter. Trust it when nothing is filtered; otherwise let DynamicTable
   * derive the denominator from `hasNextPage` (a short page is provably the last one).
   */
  const totalPages = useMemo(() => {
    if (totalRecords == null) return null;
    if (!totalRecords) return 1;
    return Math.max(1, Math.ceil(totalRecords / listPageSize) || 1);
  }, [totalRecords, listPageSize]);

  /**
   * Only needed when the exact total is unavailable (the count probe hit its cap). With a
   * real `totalPages` the table disables Next on the last page by itself.
   */
  const hasNextPage = totalPages == null ? rows.length >= listPageSize : undefined;

  const headers = [
    { key: "name", label: "Consultation ID" },
    { key: "patient", label: "Patient" },
    { key: "doctor_name", label: clinicianFilterLabel(kind) },
    { key: "date", label: "Date" },
    { key: "consultation_type", label: "Type" },
    { key: "stateCell", label: "State" },
    { key: "notesAddedCell", label: "Notes Added" },
  ];

  const handleRowClick = async (row) => {
    if (typeof onSelectConsultation !== "function") return;
    const consultationId = row.id;
    if (consultationId == null) return;

    let patientId = row.patient_id?.[0] ?? null;
    let doctorIdForNotes = null;

    try {
      if (kind === "psychiatrist") {
        const detail = await patientService.fetchConsultationById(consultationId);
        if (detail?.patient_id?.[0] != null) patientId = detail.patient_id[0];
        const dr = detail?.psychiatrist_id;
        if (dr != null) {
          doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
        }
      } else if (isAlliedSessionKind(kind)) {
        const objectKey = ALLIED_ODOO_OBJECT[kind];
        const detail = await patientService.fetchOdooSessionRecordById(
          objectKey,
          consultationId,
        );
        if (detail?.patient_id?.[0] != null) patientId = detail.patient_id[0];
        const dr = detail?.clinical_psychologist_id;
        if (dr != null) {
          doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
        }
      } else {
        const detail = await patientService.fetchPsychologistSessionById(consultationId);
        if (detail?.patient_id?.[0] != null) patientId = detail.patient_id[0];
        const dr = detail?.clinical_psychologist_id;
        if (dr != null) {
          doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
        }
      }
    } catch (e) {
      console.warn("[ConsultationListSection] detail fetch fallback", e);
      if (kind === "psychiatrist" && row.psychiatrist_id) {
        const dr = row.psychiatrist_id;
        doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
      }
      if (
        (kind === "psychologist" || isAlliedSessionKind(kind)) &&
        row.clinical_psychologist_id
      ) {
        const dr = row.clinical_psychologist_id;
        doctorIdForNotes = Array.isArray(dr) ? String(dr[0]) : String(dr);
      }
    }

    onSelectConsultation({
      kind,
      consultationId,
      patientId,
      noteType: noteKindForGeneratedNotes(kind),
      doctorIdForNotes,
    });
  };

  const closeViewNoteModal = useCallback(() => {
    setViewNoteModalOpen(false);
    setViewNotePayload(null);
  }, []);

  const handleViewGeneratedNote = useCallback(
    async (row) => {
      const consultationId = row.id;
      if (consultationId == null) return;

      setViewNoteModalOpen(true);
      setViewNotePayload({
        id: String(consultationId),
        type: "…",
        typeRaw: undefined,
        note: "Loading…",
        created: "—",
        updated: "—",
      });

      try {
        const raw = await fetchGeneratedNoteDocument(
          db,
          consultationId,
          noteKindForGeneratedNotes(kind),
          row.patient_id?.[0],
        );
        if (!raw) {
          setViewNotePayload({
            id: String(consultationId),
            type: "N/A",
            typeRaw: undefined,
            note: "No generated note document was found for this consultation.",
            created: "N/A",
            updated: "N/A",
          });
          return;
        }
        setViewNotePayload(buildNoteForModal(raw, consultationId));
      } catch (e) {
        console.warn("[ConsultationListSection] view note fetch", e);
        setViewNotePayload({
          id: String(consultationId),
          type: "Error",
          typeRaw: undefined,
          note: e?.message || "Failed to load the generated note.",
          created: "N/A",
          updated: "N/A",
        });
      }
    },
    [buildNoteForModal, db, kind],
  );

  const selectedPatientOption = useMemo(() => {
    if (filters.patientId == null || filters.patientId === "") return null;
    const key = String(filters.patientId);
    return patientSelectOptions.find((o) => String(o.value) === key) ?? null;
  }, [filters.patientId, patientSelectOptions]);

  const tableActions = useMemo(
    () => [
      {
        label: "View note",
        showIf: (row) => row.notesAdded === "Yes",
        onClick: handleViewGeneratedNote,
      },
    ],
    [handleViewGeneratedNote],
  );

  const clearFilters = () => {
    setFilters(emptyFilters());
    setGroupByFields([]);
    setCurrentPage(1);
  };

  return (
    <Box sx={{ mb: 3 }}>
      {error && (
        <div className={styles.errorBanner} style={{ marginBottom: 16 }}>
          <p>{error}</p>
          <button type="button" onClick={() => fetchList()}>
            Retry
          </button>
        </div>
      )}

      <Paper
        elevation={0}
        sx={{
          mb: 2,
          p: 2,
          border: "1px solid",
          borderColor: "divider",
          bgcolor: "background.paper",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <FilterAltOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
          <Typography variant="subtitle2" color="text.secondary">
            Filters
          </Typography>
        </Stack>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "flex-end",
          }}
        >
        <FormControl size="small" sx={{ minWidth: 140 }}>
          <InputLabel id={`date-${kind}`}>Date</InputLabel>
          <Select
            labelId={`date-${kind}`}
            label="Date"
            value={filters.date}
            onChange={(e) => {
              const v = e.target.value;
              setFilters((prev) => ({
                ...prev,
                date: v,
                selectedDate: v === "custom" ? prev.selectedDate : null,
                dateFrom: v === "custom_range" ? prev.dateFrom : null,
                dateTo: v === "custom_range" ? prev.dateTo : null,
              }));
              setCurrentPage(1);
            }}
          >
            <MenuItem value="all">All dates</MenuItem>
            <MenuItem value="today">Today</MenuItem>
            <MenuItem value="this_week">This week</MenuItem>
            <MenuItem value="this_month">This month</MenuItem>
            <MenuItem value="custom">Custom date</MenuItem>
            <MenuItem value="custom_range">Custom range</MenuItem>
          </Select>
        </FormControl>

        {filters.date === "custom" && (
          <TextField
            size="small"
            label="Day"
            type="date"
            InputLabelProps={{ shrink: true }}
            value={filters.selectedDate || ""}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, selectedDate: e.target.value }));
              setCurrentPage(1);
            }}
          />
        )}

        {filters.date === "custom_range" && (
          <>
            <TextField
              size="small"
              label="From"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateFrom || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateFrom: e.target.value }));
                setCurrentPage(1);
              }}
              inputProps={filters.dateTo ? { max: filters.dateTo } : undefined}
            />
            <TextField
              size="small"
              label="To"
              type="date"
              InputLabelProps={{ shrink: true }}
              value={filters.dateTo || ""}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, dateTo: e.target.value }));
                setCurrentPage(1);
              }}
              inputProps={filters.dateFrom ? { min: filters.dateFrom } : undefined}
            />
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel id={`state-${kind}`}>State</InputLabel>
          <Select
            labelId={`state-${kind}`}
            label="State"
            value={filters.state}
            onChange={(e) => {
              setFilters((prev) => ({ ...prev, state: e.target.value }));
              setCurrentPage(1);
            }}
          >
            {MINDTALK_LIST_STATE_FILTER_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {!isAlliedSessionKind(kind) && (
          <>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel id={`channel-${kind}`}>Consultation type</InputLabel>
              <Select
                labelId={`channel-${kind}`}
                label="Consultation type"
                value={filters.consultationChannel}
                onChange={(e) => {
                  setFilters((prev) => ({
                    ...prev,
                    consultationChannel: e.target.value,
                  }));
                  setCurrentPage(1);
                }}
              >
                <MenuItem value="all">All (in-person and online)</MenuItem>
                <MenuItem value="in_person">In-person</MenuItem>
                <MenuItem value="online">Online</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id={`campus-${kind}`}>{subcampusSelectLabel}</InputLabel>
              <Select
                labelId={`campus-${kind}`}
                label={subcampusSelectLabel}
                value={filters.campusId || ""}
                onChange={(e) => {
                  setFilters((prev) => ({ ...prev, campusId: e.target.value }));
                  setCurrentPage(1);
                }}
              >
                {campusOptions.map((opt) => (
                  <MenuItem key={opt.value || "all"} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </>
        )}

        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id={`doc-${kind}`}>{clinicianFilterLabel(kind)}</InputLabel>
          <Select
            labelId={`doc-${kind}`}
            label={clinicianFilterLabel(kind)}
            value={
              filters.doctorId != null && filters.doctorId !== ""
                ? String(filters.doctorId)
                : ""
            }
            onChange={(e) => {
              const v = e.target.value;
              setFilters((prev) => ({ ...prev, doctorId: v === "" ? null : v }));
              setCurrentPage(1);
            }}
          >
            {doctorOptions.map((opt) => (
              <MenuItem
                key={opt.value === null ? "all" : String(opt.value)}
                value={opt.value === null ? "" : String(opt.value)}
              >
                {opt.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Autocomplete
          size="small"
          sx={{ minWidth: 280 }}
          options={patientSelectOptions}
          value={selectedPatientOption}
          onChange={(_, newValue) => {
            setFilters((prev) => ({ ...prev, patientId: newValue?.value ?? null }));
            setCurrentPage(1);
          }}
          getOptionLabel={(option) => option?.label ?? ""}
          isOptionEqualToValue={(a, b) =>
            a != null && b != null && String(a.value) === String(b.value)
          }
          renderInput={(params) => (
            <TextField {...params} label="Patient" placeholder="Search or select…" />
          )}
          noOptionsText="No matching patients"
        />

        <FormControl size="small" sx={{ minWidth: 280, maxWidth: 360 }}>
          <InputLabel id={`groupby-${kind}`}>Group by</InputLabel>
          <Select
            labelId={`groupby-${kind}`}
            label="Group by"
            multiple
            value={groupByFields}
            onChange={(e) => {
              const next =
                typeof e.target.value === "string"
                  ? e.target.value.split(",")
                  : e.target.value;
              setGroupByFields(sortGroupByFields(next));
              setCurrentPage(1);
            }}
            renderValue={(selected) =>
              selected.length === 0
                ? "None"
                : sortGroupByFields(selected)
                    .map(
                      (k) =>
                        groupByMenuItems.find((o) => o.value === k)?.label ?? k,
                    )
                    .join(" → ")
            }
            MenuProps={{ closeOnSelect: false }}
          >
            {groupByMenuItems.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox
                  checked={groupByFields.indexOf(opt.value) > -1}
                  size="small"
                />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button variant="outlined" size="small" onClick={clearFilters}>
          Clear filters
        </Button>
        </Box>
      </Paper>

      <DynamicTable
        title={sectionTitle}
        headers={headers}
        data={rows}
        actions={tableActions}
        onRowClick={handleRowClick}
        currentPage={currentPage}
        totalPages={totalPages}
        hasNextPage={hasNextPage}
        emptyMessage={
          currentPage > 1
            ? "No more data available for the current filters."
            : undefined
        }
        onPageChange={(p) => setCurrentPage(p)}
        isLoading={loading}
        groupByLevels={groupByLevels.length ? groupByLevels : null}
        groupByResetKey={groupByFields.join("|")}
      />

      {viewNotePayload != null && (
        <NoteModal
          isOpen={viewNoteModalOpen}
          onClose={closeViewNoteModal}
          note={viewNotePayload}
        />
      )}
    </Box>
  );
}
